import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './accessibilityDialog.module.scss'
import Dialog from '@/components/_custom/Dialog/Dialog'
import { FunctionComponent, useState } from 'react'
import { useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Accessibility, AccessibilityType, Daycare } from '@/types/Daycare'
import {
    CREATE_DAYCARE_ACCESSIBILITY,
    UPDATE_DAYCARE_ACCESSIBILITY
} from '@/modules/API/daycare/daycareAccessibilityHandler'
import moment from 'moment/moment'
import TimePicker from '@/components/_custom/Picker/timePicker/timePicker'
import { useAuth } from '@/modules/context/AuthContext'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import {
    getClosingForAccessibility,
    getOpeningForAccessibility
} from '@/container/daycare/daycareProfile/merger/accessibility/AccessibilitiesListsRow.tsx'

interface AccessibilityDialogProps {
    daycare: Daycare
    isDialogOpen: boolean
    create: boolean
    handleCloseDialog: () => void
    accessibilities: Array<Accessibility>
    // ADD
    addOneAccessibility?: (value: Accessibility) => void
    accessibilityType: AccessibilityType
    // EDIT
    accessibility?: Accessibility
    updateOneAccessibility?: (value: Accessibility) => void
}

const AccessibilityDialog: FunctionComponent<AccessibilityDialogProps> = ({
    daycare,
    isDialogOpen,
    create,
    accessibilities,
    handleCloseDialog,
    addOneAccessibility,
    accessibilityType,
    accessibility,
    updateOneAccessibility
}: AccessibilityDialogProps) => {
    const { t } = useTranslation()

    const { fetchCurrentDaycare } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [accessibilityStart, setAccessibilityStart] = useState<string>(
        accessibility
            ? moment(accessibility.start, 'HH:mm').format('HH:mm')
            : getOpeningForAccessibility(daycare, 'HH:mm')
    )
    const [accessibilityEnd, setAccessibilityEnd] = useState<string>(
        accessibility
            ? moment(accessibility.end, 'HH:mm').format('HH:mm')
            : getClosingForAccessibility(daycare, 'HH:mm')
    )

    const [createAccessibility] = useMutation(CREATE_DAYCARE_ACCESSIBILITY, {
        variables: {
            daycare_id: daycare.id,
            type: accessibilityType,
            start: accessibilityStart,
            end: accessibilityEnd
        },
        onCompleted: ({ createDaycareAccessibility }) => {
            fetchCurrentDaycare()

            if (addOneAccessibility) {
                addOneAccessibility({
                    id: createDaycareAccessibility || 0,
                    type: accessibilityType,
                    start: accessibilityStart,
                    end: accessibilityEnd
                })
            }

            setAccessibilityStart(
                accessibility
                    ? moment(accessibility.start, 'HH:mm').format('HH:mm')
                    : getOpeningForAccessibility(daycare, 'HH:mm')
            )
            setAccessibilityEnd(
                accessibility
                    ? moment(accessibility.end, 'HH:mm').format('HH:mm')
                    : getClosingForAccessibility(daycare, 'HH:mm')
            )

            showNotificationSuccess(
                accessibilityType === AccessibilityType.arrival ? t('arrivalCreated') : t('departureCreated')
            )

            handleCloseDialog()
        },
        onError: () => {
            showNotificationError(
                accessibilityType === AccessibilityType.arrival
                    ? t('errorCreatingArrival')
                    : t('errorCreatingDeparture')
            )
        }
    })

    const [updateAccessibility] = useMutation(UPDATE_DAYCARE_ACCESSIBILITY, {
        variables: {
            accessibility: {
                id: accessibility?.id,
                type: accessibilityType,
                start: accessibilityStart,
                end: accessibilityEnd
            }
        },
        onCompleted: () => {
            fetchCurrentDaycare()

            if (updateOneAccessibility) {
                updateOneAccessibility({
                    id: accessibility?.id || 0,
                    type: accessibilityType,
                    start: accessibilityStart,
                    end: accessibilityEnd
                })
            }

            setAccessibilityStart(accessibilityStart)
            setAccessibilityEnd(accessibilityEnd)

            showNotificationSuccess(
                accessibilityType === AccessibilityType.arrival ? t('arrivalUpdated') : t('departureUpdated')
            )

            handleCloseDialog()
        },
        onError: () => {
            showNotificationError(
                accessibilityType === AccessibilityType.arrival ? t('errorUpdatedArrival') : t('errorUpdatedDeparture')
            )
        }
    })

    const range: number = daycare.merger ? Number(daycare.bookRange.split('_')[1] || '15') : 15

    function getMax(format: 'HH' | 'mm'): string {
        if (accessibility) {
            const accessibilityAfter: Accessibility | undefined = accessibilities.find((accessibilityAfter) => {
                return (
                    accessibility.id !== accessibilityAfter.id &&
                    moment(accessibilityAfter.start, 'HHmm').format('HHmm') >=
                        moment(accessibility.end, 'HHmm').format('HHmm')
                )
            })
            if (accessibilityAfter) {
                return moment(accessibilityAfter.start, 'HH:mm').format(format)
            }
        }
        return getClosingForAccessibility(daycare, format)
    }

    function getMin(format: 'HH' | 'mm'): string {
        if (accessibility) {
            const accessibilityBefore: Accessibility | undefined = accessibilities.find((accessibilityBefore) => {
                return (
                    accessibility.id !== accessibilityBefore.id &&
                    moment(accessibilityBefore.end, 'HHmm').format('HHmm') <=
                        moment(accessibility.start, 'HHmm').format('HHmm')
                )
            })
            if (accessibilityBefore) {
                return moment(accessibilityBefore.end, 'HH:mm').format(format)
            }
        }
        return getOpeningForAccessibility(daycare, format)
    }

    return (
        <Dialog open={isDialogOpen} handleClose={handleCloseDialog} className={styles.Dialog}>
            <DialogHeader
                title={
                    accessibilityType === AccessibilityType.arrival
                        ? create
                            ? t('addNewArrival')
                            : t('updateArrival')
                        : create
                          ? t('addNewDeparture')
                          : t('updateDeparture')
                }
            />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <div className={styles.Input}>
                        <TimePicker
                            value={accessibilityStart}
                            setValue={setAccessibilityStart}
                            minHour={Number(getMin('HH'))}
                            minMinute={Number(getMin('mm'))}
                            maxHour={Number(moment(accessibilityEnd, 'HH:mm').format('HH'))}
                            maxMinute={Number(moment(accessibilityEnd, 'HH:mm').format('mm'))}
                            minuteToDisable={range}
                        />
                        <p>{t('to')}</p>
                        <TimePicker
                            value={accessibilityEnd}
                            setValue={setAccessibilityEnd}
                            minHour={Number(moment(accessibilityStart, 'HH:mm').format('HH'))}
                            minMinute={Number(moment(accessibilityStart, 'HH:mm').format('mm'))}
                            maxHour={Number(getMax('HH'))}
                            maxMinute={Number(getMax('mm'))}
                            minuteToDisable={range}
                        />
                    </div>
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleCloseDialog} className={styles.Button} />
                    <ButtonTextIcon
                        type={create ? 'add' : 'update'}
                        onClick={create ? createAccessibility : updateAccessibility}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default AccessibilityDialog
